<template>
    <div class="email-card">
        <template-card-v2
            :oneLog="oneLog"
            :linkedtype="linkedtype"
            :linkedid="linkedid"
            :isDocument="isDocument"
            :overviewType="overviewType"
        >
            <template #icon>
                <CompassObject
                    v-if="emailStatus.includes(oneLog.email.status)"
                    object="sent-email"
                    size="small"
                />
                <CompassObject
                    v-else-if="['replied', 'received'].includes(oneLog.email.status)"
                    object="inbox-email"
                    size="small"
                />
            </template>
            <template v-if="['replied', 'received'].includes(oneLog.email.status)" #label>
                <span
                    v-if="oneLog.email.from"
                    class="semibold gray-80 fs-2 email-card-status-sender__label"
                >
                    {{ $l10n.__('De : %(emailSender)s', { emailSender: oneLog.email.from.email }) }}
                </span>
            </template>
            <template v-else-if="oneLog.owner" #label>
                <div class="email-label-container">
                    <person-list-light :list="[oneLog.owner]"></person-list-light>
                    <email-statuses :emailInfo="oneLog.email" />
                </div>
            </template>
            <template #content>
                <div class="email-card__content">
                    <CompassAlert
                        v-if="isFailedToSend"
                        type="error"
                        :description="errorText"
                        showIcon
                        :action="{
                            label: errorButtonText,
                            callback: () => resendEmail(),
                        }"
                        :showClose="false"
                    />
                    <CompassAlert
                        v-if="isScheduledToBeSent"
                        type="neutral"
                        size="small"
                        :description="scheduledAlertDescriptionLabel"
                        :showClose="false"
                        :showIcon="false"
                        :action="{
                            label: scheduledAlertActionLabel,
                            callback: () => cancelSendElement(oneLog),
                        }"
                    />

                    <!-- email subject -->
                    <p
                        v-if="isMocked || isScheduledToBeSent"
                        class="typographies-paragraph-medium-regular email-subject"
                    >
                        {{ oneLog.email.subject }}
                    </p>
                    <CompassLink
                        v-else
                        :label="oneLog.email.subject"
                        @click="openEmailPanel(oneLog)"
                    />
                    <!-- email description -->
                    <div
                        class="typographies-paragraph-medium-regular email-description"
                        :class="editorClass"
                        v-html="descriptionEmail"
                    />
                    <div v-if="attachmentsCount > 0" class="email-card__attachments">
                        <div class="email-card__attachments-list">
                            <EmailAttachmentLink
                                v-for="attachment in attachmentsList"
                                :key="attachment.id"
                                :file="attachment"
                                :isMocked="isMocked"
                            />
                            <button
                                v-if="attachmentsCount > 3 && !isAttachmentsListExtended"
                                class="email-attachment-show-more"
                                @click="handleShowMoreClick"
                            >
                                <span class="email-attachment-show-more__label">
                                    {{ formattedAttachmentsShowMoreCount }}
                                </span>
                            </button>
                        </div>
                    </div>
                    <CompassButton
                        v-if="showConversationButton"
                        :label="$t.__('timeline.EmailCardV2.display_email')"
                        type="secondary"
                        @click="openEmailPanel(oneLog)"
                    />
                </div>
            </template>
        </template-card-v2>
    </div>
</template>

<script>
import TemplateCardV2 from './TemplateCardV2.vue';
import TimelineCardMixin from './TimelineCardMixin.js';
import PersonListLight from 'vcomponents/utilities/PersonListLight.vue';
import { createNamespacedHelpers } from 'vuex';
import EmailStatuses from './EmailStatuses.vue';
import EmailAttachmentLink from 'vcomponents/email/EmailAttachmentLink.vue';
const { mapState: mapStateUser } = createNamespacedHelpers('user');
import EmailMixins from '../../mixins/EmailMixins';
import { addBrToEmptyPragraphs, editorClass } from 'Helpers/richTextHelper.js';

export default {
    components: {
        TemplateCardV2,
        PersonListLight,
        EmailStatuses,
        EmailAttachmentLink,
    },
    mixins: [TimelineCardMixin, EmailMixins],
    props: {
        isDocument: {
            type: Boolean,
            default: false,
        },
        overviewType: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            errorText: this.$t.__('timeline.EmailCardV2.errorText'),
            errorButtonText: this.$t.__('timeline.EmailCardV2.errorButtonText'),
            scheduledAlertActionLabel: this.$t.__('common.actions.cancel_sending'),
            emailStatus: [
                'scheduled',
                'automation',
                'dunning',
                'deleted',
                'updated',
                'unlinked',
                'linked',
                'opened',
                'dropped',
                'bounced',
                'delivered',
                'toggleread',
                'failed',
                'restored',
                'sent',
                'created',
            ],
            isAttachmentsListExtended: false,
        };
    },
    computed: {
        ...mapStateUser({
            userForename: 'forename',
            userName: 'name',
        }),
        isMocked() {
            return this.oneLog.mocked;
        },
        isScheduledToBeSent() {
            return (
                this.oneLog.email.email_status === 'SCHEDULED_TO_BE_SENT' &&
                this.oneLog.email.status === 'scheduled'
            );
        },
        isFailedToSend() {
            return this.oneLog.email.email_status === 'FAILED_TO_SEND';
        },
        attachmentsCount() {
            return this.oneLog.email.attachments && this.oneLog.email.attachments.length;
        },
        attachmentsList() {
            if (this.oneLog.email.attachments.length > 3 && !this.isAttachmentsListExtended) {
                return this.oneLog.email.attachments.slice(0, 3);
            }

            return this.oneLog.email.attachments;
        },
        formattedAttachmentsShowMoreCount() {
            return this.$l10n.n__('+%(n)d autres', '+%(n)d autres', this.attachmentsCount - 3, {
                n: this.attachmentsCount - 3,
            });
        },
        showConversationButton() {
            return !this.isMocked && !this.isFailedToSend && !this.isScheduledToBeSent;
        },
        scheduledAlertDescriptionLabel() {
            const timestamp = moment.unix(this.oneLog.timestamp);
            const formattedDate = timestamp.format('DD MMMM YYYY');
            const formattedTime = timestamp.format('HH:mm');
            return this.$t.__('email.emailCardV2.send_programmed_for', {
                date: formattedDate,
                time: formattedTime,
            });
        },
        editorClass,
        descriptionEmail() {
            return addBrToEmptyPragraphs(this.oneLog.email.description);
        },
    },
    methods: {
        resendEmail() {
            if (this.oneLog.email.sending_type) {
                EventBus.emit('emailConsultPanel:new', { params: [{ oneLog: this.oneLog }] });
            } else {
                EventBus.emit('emailPanel:resend', { params: [this.oneLog] });
            }
        },
        openEmailPanel(data) {
            if (data.objectid === this.oneLog.objectid && !this.isFailedToSend) {
                EventBus.emit('emailConsultPanel:new', { params: [{ oneLog: this.oneLog }] });
            }
        },
        handleShowMoreClick() {
            this.isAttachmentsListExtended = !this.isAttachmentsListExtended;
        },
        cancelSendElement(mail) {
            this.open({
                key: 'emailDeleteConfirmDialog',
                props: {
                    email: {
                        ...this.oneLog.email,
                        id: mail.objectid,
                    },
                },
            });
        },
    },
};
</script>

<style lang="scss">
.email-card.highlighted {
    .activity-card__container {
        box-shadow: var(--box-shadows-small);
    }
}
.email-description {
    & > div p:first-child {
        margin-top: 0;
    }

    & > div p:last-child {
        margin-bottom: 0;
    }
    p {
        margin: 0;
    }
}
</style>

<style lang="scss" scoped>
.email-card-status__container span,
.email-card-status-sender__label span {
    line-height: 16px;
}

.email-card {
    word-break: break-word;

    .email-label-container {
        display: flex;
        flex-direction: row;
        gap: var(--spacing-1);
    }

    .email-description,
    .email-subject {
        margin: 0 0 var(--spacing-0);
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 20;
        overflow: hidden;

        :deep(ul) {
            padding-left: 24px;
            list-style-type: disc;
        }
    }

    &__content {
        .compass-alert--error {
            border: none;

            .compass-alert__wrapper {
                padding-top: var(--spacing-0, 8px);
                padding-bottom: var(--spacing-0, 8px);
            }
        }
    }

    &__draft {
        align-items: center;
        background: var(--colors-common-background-subtle);
        border-radius: 4px;
        display: flex;
        justify-content: space-between;
        margin: 0 0 var(--spacing-1);
        padding: var(--spacing-0) var(--spacing-0) var(--spacing-0) var(--spacing-1);

        &__left-container {
            align-items: center;
            display: flex;
            gap: var(--spacing-1);
        }

        &__text-container {
            color: var(--colors-common-text-subtle-default);
        }

        &__icon {
            flex-shrink: 0;
            fill: var(--colors-common-icon-neutral);
        }
    }

    &__attachments {
        margin: var(--spacing-3) 0;
        padding: var(--spacing-2) 0 0;

        &-list {
            display: flex;
            flex-wrap: wrap;
            gap: var(--spacing-0);
        }
    }

    .email-attachment-show-more {
        align-items: center;
        background: transparent;
        border: 1px solid var(--colors-common-border-default);
        border-radius: 50px;
        color: var(--colors-common-text-subtle-default);
        cursor: pointer;
        display: inline-flex;
        font-family: var(--font-families-lato);
        font-size: 14px;
        font-weight: 600;
        gap: var(--spacing-0);
        line-height: 20px;
        max-width: 180px;
        padding: var(--spacing-0) var(--spacing-3);

        &__label {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }
}
</style>
